import React, { useMemo } from "react";

import Box from "components/common/Box";
import Stack from "components/common/Stack";
import Text from "components/common/Text";
import Button from "components/common/Button";
import { formatHelper } from "helper/format";

const ACCENT = "#C06A26";

const ICONS = {
  tick: "/images/atlas-product-info/pricing/tick.svg",
  arrowOutline: "/images/atlas-product-info/pricing/arrow-outline.svg",
  arrowSolid: "/images/atlas-product-info/pricing/arrow-solid.svg",
  star: "/images/atlas-product-info/pricing/star.svg",
};

const FALLBACK_TIERS = [
  {
    id: "global",
    title: "Global Equities + Commodities",
    planPrice: 100000,
    options: [
      {
        title: "Global Equities + Commodities",
        features: [
          "Global Equities (US | UK | GER | OTHERS)",
          "Commodities (MCX + Global)",
        ],
        cta: "Get Global Access",
      },
      {
        title: "Indian Equities + Commodities",
        features: [
          "Indian Equities (Buy & Sell Side)",
          "Commodities (MCX + Global)",
        ],
        cta: "Get Indian Access",
      },
    ],
  },
  {
    id: "allInOne",
    title: "Global Equities + Commodities + Indian Equities",
    eyebrow: "ALL IN ONE ACCESS",
    planPrice: 150000,
    featured: true,
    features: [
      "Global Equities (US | UK | GER | OTHERS)",
      "Commodities (MCX + Global)",
      "Indian Equities (Buy & Sell Side)",
      "Everything in One",
    ],
    cta: "Get All in One Access",
  },
  {
    id: "indian",
    title: "Indian Equities",
    planPrice: 70000,
    features: [
      "Indian Equities (Buy & Sell Side)",
      "Holdings tracker with live LTP via Kite",
      "Research rationale + chart attachments",
      "Annual subscription",
    ],
    cta: "Get Indian Access",
  },
];

const planLabel = (plan = {}) =>
  `${plan.planName || ""} ${plan.planCode || ""}`.toUpperCase();

const matchTier = (plan, tierId) => {
  const n = planLabel(plan);
  if (tierId === "allInOne") {
    return n.includes("ALL") || (n.includes("GLOBAL") && n.includes("INDIAN"));
  }
  if (tierId === "indian") {
    return n.includes("INDIAN") && !n.includes("GLOBAL") && !n.includes("ALL");
  }
  if (tierId === "global") {
    return n.includes("GLOBAL") && !n.includes("INDIAN") && !n.includes("ALL");
  }
  return false;
};

const resolveTiers = (plans = []) => {
  const fromApi = Array.isArray(plans) ? plans.filter(Boolean) : [];
  const used = new Set();

  return FALLBACK_TIERS.map((fallback, index) => {
    let apiPlan =
      fromApi.find((p, i) => !used.has(i) && matchTier(p, fallback.id)) || null;

    if (!apiPlan && fromApi[index] && !used.has(index)) {
      apiPlan = fromApi[index];
    }

    if (apiPlan) {
      const apiIndex = fromApi.indexOf(apiPlan);
      if (apiIndex >= 0) used.add(apiIndex);
    }

    const checklist = Array.isArray(apiPlan?.planChecklist)
      ? apiPlan.planChecklist.filter(Boolean)
      : [];

    return {
      ...fallback,
      ...apiPlan,
      id: fallback.id,
      title: fallback.title,
      eyebrow: fallback.eyebrow,
      featured: fallback.featured,
      options: fallback.options,
      features: checklist.length ? checklist : fallback.features,
      cta: fallback.cta,
      planPrice:
        Number(apiPlan?.planPrice ?? fallback.planPrice) || fallback.planPrice,
    };
  });
};

const IconImg = ({ src, size }) => (
  <Box
    component="img"
    src={src}
    alt=""
    sx={{
      width: size,
      height: size,
      display: "block",
      flexShrink: 0,
      objectFit: "contain",
    }}
  />
);

const Divider = () => (
  <Stack direction="row" spacing={1.5} alignItems="center" sx={{ width: "100%" }}>
    <Box sx={{ flex: 1, height: "1px", bgcolor: "#E5E7EB" }} />
    <Box sx={{ width: 10, height: 10, borderRadius: "50%", bgcolor: ACCENT }} />
    <Box sx={{ flex: 1, height: "1px", bgcolor: "#E5E7EB" }} />
  </Stack>
);

const FeatureRow = ({ text }) => (
  <Stack direction="row" spacing={1.5} alignItems="center" sx={{ width: "100%" }}>
    <IconImg src={ICONS.tick} size={20} />
    <Text
      sx={{
        flex: 1,
        minWidth: 0,
        fontSize: 15,
        fontWeight: 400,
        color: "#374151",
        lineHeight: "22px",
      }}
    >
      {text}
    </Text>
  </Stack>
);

const OutlineCta = ({ label, onClick }) => (
  <Button
    onClick={onClick}
    endIcon={<IconImg src={ICONS.arrowOutline} size={24} />}
    sx={{
      width: "100%",
      height: 56,
      borderRadius: "14px",
      textTransform: "none",
      fontWeight: 700,
      fontSize: 15,
      lineHeight: "20px",
      color: ACCENT,
      bgcolor: "#fff",
      border: `1px solid ${ACCENT}`,
      boxShadow: "none",
      px: "18px",
      py: 2,
      "&:hover": { bgcolor: "#fff", opacity: 0.92 },
      "& .MuiButton-endIcon": { ml: "10px", mr: 0 },
    }}
  >
    {label}
  </Button>
);

const SolidCta = ({ label, onClick }) => (
  <Button
    onClick={onClick}
    endIcon={<IconImg src={ICONS.arrowSolid} size={24} />}
    sx={{
      width: "100%",
      height: 56,
      borderRadius: "14px",
      textTransform: "none",
      fontWeight: 700,
      fontSize: 15,
      lineHeight: "20px",
      color: "#fff",
      bgcolor: ACCENT,
      boxShadow: "0 12px 12px rgba(192,106,38,0.27)",
      px: "18px",
      py: 2,
      "&:hover": { bgcolor: ACCENT, opacity: 0.95 },
      "& .MuiButton-endIcon": { ml: "10px", mr: 0 },
    }}
  >
    {label}
  </Button>
);

const OptionBlock = ({ title, features, cta, onUnlock }) => (
  <Stack
    spacing={1.5}
    sx={{
      p: 2,
      width: "100%",
      borderRadius: "16px",
      bgcolor: "#F9FAFB",
      border: "1px solid #E5E7EB",
      boxSizing: "border-box",
    }}
  >
    <Text
      sx={{
        fontSize: 16,
        fontWeight: 700,
        color: "#111827",
        lineHeight: "22px",
        whiteSpace: "nowrap",
      }}
    >
      {title}
    </Text>
    <Stack spacing={1.25} sx={{ width: "100%" }}>
      {features.map((f) => (
        <FeatureRow key={f} text={f} />
      ))}
    </Stack>
    <OutlineCta label={cta} onClick={() => onUnlock?.({ title, cta })} />
  </Stack>
);

const TierCard = ({ children, featured = false, sx = {} }) => (
  <Box
    sx={{
      position: "relative",
      flex: 1,
      minWidth: 0,
      height: "100%",
      p: { xs: 3, md: 4 },
      borderRadius: "24px",
      bgcolor: "#fff",
      border: featured ? `2px solid ${ACCENT}` : "1px solid #E5E7EB",
      boxShadow: "0 18px 20px rgba(0,0,0,0.03), 0 2px 5px rgba(0,0,0,0.04)",
      display: "flex",
      flexDirection: "column",
      boxSizing: "border-box",
      ...sx,
    }}
  >
    {children}
  </Box>
);

const PriceBlock = ({ price }) => (
  <Stack spacing={0.75} alignItems="center" sx={{ width: "100%" }}>
    <Text
      sx={{
        fontSize: { xs: 36, md: 44 },
        fontWeight: 700,
        color: "#111827",
        lineHeight: "48px",
        whiteSpace: "nowrap",
      }}
    >
      {price}
    </Text>
    <Text sx={{ fontSize: 14, fontWeight: 500, color: "#6B7280", lineHeight: "20px" }}>
      (Excl. GST)
    </Text>
  </Stack>
);

const OrBadge = () => (
  <Stack
    direction="row"
    alignItems="center"
    justifyContent="center"
    sx={{ width: "100%", height: 36, py: 0.5 }}
  >
    <Box
      sx={{
        px: 1.75,
        height: 26,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        borderRadius: 999,
        bgcolor: "#fff",
        border: "1px solid #E5E7EB",
      }}
    >
      <Text sx={{ fontSize: 14, fontWeight: 800, color: "#6B7280", lineHeight: "20px" }}>
        OR
      </Text>
    </Box>
  </Stack>
);

const BestValueBadge = () => (
  <Box
    sx={{
      position: "absolute",
      top: -16,
      right: 14,
      display: "flex",
      alignItems: "center",
      gap: 1,
      px: 1.5,
      py: 1,
      borderRadius: 999,
      bgcolor: "#fff",
      border: `1px solid ${ACCENT}`,
      boxShadow: "0 6px 8px rgba(0,0,0,0.07)",
      zIndex: 1,
    }}
  >
    <IconImg src={ICONS.star} size={16} />
    <Text
      sx={{
        fontSize: 12,
        fontWeight: 800,
        color: ACCENT,
        lineHeight: 1,
        whiteSpace: "nowrap",
      }}
    >
      BEST VALUE
    </Text>
  </Box>
);

const formatPrice = (planPrice) =>
  formatHelper.currency(planPrice, {
    config: { maximumFractionDigits: 0, minimumFractionDigits: 0 },
  });

const GlobalTierCard = ({ tier, onUnlock }) => {
  const price = formatPrice(tier.planPrice);
  return (
    <TierCard>
      <Stack spacing={2.5} sx={{ width: "100%" }}>
        <Text
          sx={{
            fontSize: { xs: 18, md: 20 },
            fontWeight: 700,
            color: "#111827",
            lineHeight: "26px",
            textAlign: "center",
            width: "100%",
          }}
        >
          {tier.title}
        </Text>
        <Divider />
        <PriceBlock price={price} />
        {(tier.options || []).map((option, index) => (
          <React.Fragment key={option.title}>
            {index > 0 ? <OrBadge /> : null}
            <OptionBlock
              title={option.title}
              features={option.features}
              cta={option.cta}
              onUnlock={onUnlock}
            />
          </React.Fragment>
        ))}
      </Stack>
    </TierCard>
  );
};

const FeaturedTierCard = ({ tier, onUnlock, sx }) => {
  const price = formatPrice(tier.planPrice);
  return (
    <TierCard featured sx={sx}>
      <BestValueBadge />
      <Stack
        spacing={0}
        justifyContent="space-between"
        sx={{ height: "100%", flex: 1, width: "100%" }}
      >
        <Stack spacing={2.5} sx={{ width: "100%" }}>
          <Text
            sx={{
              fontSize: { xs: 13, md: 14 },
              fontWeight: 700,
              color: "#6B7280",
              textAlign: "center",
              textTransform: "uppercase",
              width: "100%",
            }}
          >
            {tier.eyebrow}
          </Text>
          <Text
            sx={{
              fontSize: { xs: 18, md: 22 },
              fontWeight: 700,
              color: "#111827",
              lineHeight: { xs: "26px", md: "28px" },
              textAlign: "center",
              width: "100%",
            }}
          >
            {tier.title}
          </Text>
          <Divider />
          <PriceBlock price={price} />
          <Stack spacing={1.5} sx={{ width: "100%" }}>
            {(tier.features || []).map((f) => (
              <FeatureRow key={f} text={f} />
            ))}
          </Stack>
        </Stack>
        <SolidCta
          label={tier.cta}
          onClick={() => onUnlock?.({ title: "All in One", cta: tier.cta })}
        />
      </Stack>
    </TierCard>
  );
};

const IndianTierCard = ({ tier, onUnlock }) => {
  const price = formatPrice(tier.planPrice);
  return (
    <TierCard>
      <Stack
        spacing={0}
        justifyContent="space-between"
        sx={{ height: "100%", flex: 1, width: "100%" }}
      >
        <Stack spacing={2.5} sx={{ width: "100%" }}>
          <Text
            sx={{
              fontSize: { xs: 20, md: 22 },
              fontWeight: 700,
              color: "#111827",
              lineHeight: "28px",
              textAlign: "center",
              width: "100%",
            }}
          >
            {tier.title}
          </Text>
          <Divider />
          <PriceBlock price={price} />
          <Stack spacing={2.5} sx={{ width: "100%" }}>
            {(tier.features || []).map((f) => (
              <FeatureRow key={f} text={f} />
            ))}
          </Stack>
        </Stack>
        <OutlineCta
          label={tier.cta}
          onClick={() => onUnlock?.({ title: tier.title, cta: tier.cta })}
        />
      </Stack>
    </TierCard>
  );
};

const AtlasPricingSection = ({ plans = [], onUnlock }) => {
  const tiers = useMemo(() => resolveTiers(plans), [plans]);
  const globalTier = tiers.find((t) => t.id === "global") || FALLBACK_TIERS[0];
  const allInOneTier = tiers.find((t) => t.id === "allInOne") || FALLBACK_TIERS[1];
  const indianTier = tiers.find((t) => t.id === "indian") || FALLBACK_TIERS[2];

  return (
    <>
      {/* Desktop */}
      <Box
        sx={{
          display: { xs: "none", md: "grid" },
          gridTemplateColumns: "1fr 1fr 1fr",
          gap: 3,
          width: "100%",
          alignItems: "stretch",
          pt: 3,
        }}
      >
        <GlobalTierCard tier={globalTier} onUnlock={onUnlock} />
        <FeaturedTierCard tier={allInOneTier} onUnlock={onUnlock} />
        <IndianTierCard tier={indianTier} onUnlock={onUnlock} />
      </Box>

      {/* Mobile — stacked tiers */}
      <Stack spacing={2.5} sx={{ display: { xs: "flex", md: "none" }, width: "100%" }}>
        <Stack spacing={1.5} alignItems="center" sx={{ px: 1, mb: 1 }}>
          <Text
            sx={{
              fontSize: 32,
              fontWeight: 800,
              color: "#05070A",
              textAlign: "center",
              lineHeight: 1.2,
            }}
          >
            Pricings
          </Text>
          <Text
            sx={{
              fontSize: 16,
              fontWeight: 400,
              color: "#94A3B8",
              textAlign: "center",
              lineHeight: "26px",
            }}
          >
            Choose the plan that fits your investing style
          </Text>
        </Stack>

        <GlobalTierCard tier={globalTier} onUnlock={onUnlock} />
        <FeaturedTierCard tier={allInOneTier} onUnlock={onUnlock} sx={{ mt: 1 }} />
        <IndianTierCard tier={indianTier} onUnlock={onUnlock} />
      </Stack>
    </>
  );
};

export default AtlasPricingSection;
